import { getRecords } from "../../api/useRecords";
import { Session } from "../../types/records.types";
import { NavigateFunction } from "react-router-dom";
import { Notifications } from "../../store/store.types";
import { createRecord } from "../../api/useCreateRecord";
import { NeurofeedbackData } from "../neurofeedback-zscore-sessions/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExtendedSessionType } from "./components/send-meeting-to-client/SendMeetingButton";
import axios from "axios";
import { MAKE_WEBHOOKS } from "../../utils/constants";
import { FormatedFields } from "../../types/fields.types";
import { groupBy } from "lodash";
import { format, parseISO } from "date-fns";

export const createNFBSession = async (session: Session, navigate: NavigateFunction, notifications: Notifications) => {
	const treatment = session?.treatment;
	try {
		const lastActiveProtocol = await getRecords("protocols", "*", {
			extraOptions: {
				filter: {
					_and: [
						{
							treatment: {
								_eq: treatment?.id
							}
						},
						{
							status: {
								_eq: "active"
							}
						}
					]
				},
				sort: "-date_created",
				pageSize: 1
			}
		});

		if (lastActiveProtocol.items.length === 0) {
			notifications.setNotification({
				message: "Não foi possível criar a sessão de neurofeedback. Protocolo ainda não definido",
				severity: "error",
				isOpen: true
			});
			return;
		}

		const allNFBSessionsDone = await getRecords("sessions", "*", {
			extraOptions: {
				filter: {
					_and: [
						{
							service: {
								_eq: 33
							}
						},
						{
							status: {
								_eq: "done"
							}
						},
						{
							treatment: {
								_eq: treatment?.id
							}
						},
						{
							id: {
								_neq: session?.id
							}
						}
					]
				}
			}
		});

		const nfbSessionNumber = allNFBSessionsDone.items.length;
		const lastActiveProtocolId = lastActiveProtocol?.items?.[0]?.id;

		// Prepare data object with required fields
		const sessionData: NeurofeedbackData = {
			session: session?.id,
			session_number: nfbSessionNumber + 1,
			protocol: lastActiveProtocolId
		};

		const sessionToAskFeedbackFromClient = 10;
		if (nfbSessionNumber + 1 === sessionToAskFeedbackFromClient) {
			sessionData.notes = `<h4 style="color: red;">-------   É a sessão nº ${sessionToAskFeedbackFromClient}. Não esquecer de pedir feedback ao cliente.   -------</h4>`;
		}

		const response = await createRecord({
			entity: "neurofeedback_data",
			data: sessionData
		});

		navigate(`/neurofeedback_data/${response.id}/update`);
	} catch (error) {
		notifications.setNotification({
			message: "Erro ao criar sessão de neurofeedback",
			severity: "error",
			isOpen: true
		});
	}
};

export const createEEGSession = async (session: Session, navigate: NavigateFunction, notifications: Notifications) => {
	if (!session?.treatment) {
		notifications.setNotification({
			message: "Não foi possível criar os dados de qEEG. Certifique-se de que a lead foi convertida para cliente.",
			severity: "warning",
			isOpen: true
		});
		return;
	}

	const treatmentId = session?.treatment.id;
	const sessionId = session?.id;

	try {
		const response = await createRecord({
			entity: "evaluations",
			data: {
				eeg_session: sessionId,
				treatment: treatmentId
			}
		});

		navigate(`/evaluations/${response.id}/update`);
	} catch (error) {
		notifications.setNotification({
			message: "Erro ao criar sessão de qEEG",
			severity: "error",
			isOpen: true
		});
	}
};

export const getButtonText = (
	nfbSession: any,
	eegSession: any,
	calendarServiceId: any,
	nfbService: number,
	eegServices: Array<number>
) => {
	// Check if this is a neurofeedback service
	if (calendarServiceId === nfbService) {
		if (nfbSession) {
			return "Dados de neurofeedback sessão nº " + nfbSession?.session_number;
		} else {
			return "Criar dados neurofeedback";
		}
	}

	// Check if this is an EEG service
	if (eegServices.includes(calendarServiceId)) {
		if (eegSession) {
			return "Dados de qEEG";
		} else {
			return "Criar dados de qEEG";
		}
	}
};

type SessionWithWrapper = {
	item: Session;
	[key: string]: any;
};

export const useUpdateMeetingCache = (sessionId: number) => {
	const queryClient = useQueryClient();
	const sessionRecordKeyPrefix = `sessions-${sessionId}-`;

	return ({ meeting_url, meeting_id }: { meeting_url: string; meeting_id: string }) => {
		queryClient.setQueriesData(
			{
				predicate: (query) => {
					const key = query.queryKey[0];
					return typeof key === "string" && key.startsWith(sessionRecordKeyPrefix);
				}
			},
			(oldData: SessionWithWrapper | undefined) => {
				if (!oldData?.item) return oldData;

				return {
					...oldData,
					item: {
						...oldData.item,
						meeting_url: meeting_url,
						meeting_id: meeting_id
					}
				};
			}
		);
	};
};

export const useGetGoogleMeeting = (sessionId: number) => {
	const queryK = `sessions-${sessionId}-meeting_id`;

	return useQuery({
		queryKey: [queryK],
		queryFn: async () => {
			const result = await getMeetingById(sessionId);
			return result;
		},
		enabled: !!sessionId,
		staleTime: 6000 // otherwise it fetches twice bc parent useEffs; can be lower
	});
};

const getMeetingById = async (sessionId: number) => {
	if (!sessionId) return;
	try {
		const result = await axios.get(`${MAKE_WEBHOOKS.google_meet}?id=${sessionId}&to_validate=true`);

		if (result.status === 200) return { exists: true, data: result.data }; // it exists on Google Calendar, return title, start_date, end_date
		return { exists: false };
	} catch (error) {
		const errorMessage =
			axios.isAxiosError(error) && error.response?.data
				? error.response.data
				: "Erro ao procurar Consulta/Reunião no Google Calendar";
		return { exists: false, error: errorMessage };
	}
};

export const prepareMeetingData = (session: ExtendedSessionType, additionalEmails?: Array<string>) => {
	const doctorTechnician = session.technician?.user_id
		? `- Dr(a). ${session.technician.user_id.first_name} ${session.technician.user_id.last_name}`
		: "";

	const isTeamMeeting = !session?.service;
	const sessionTitle = isTeamMeeting ? "Reunião" : session?.service.name;

	const safeAdditionalEmails = additionalEmails ?? [];
	const emails = isTeamMeeting
		? [...safeAdditionalEmails]
		: [session.treatment?.client?.user_id?.email, ...safeAdditionalEmails].filter((email) => email != null);

	const processedData = {
		session: {
			id: session.id,
			title: `NeuroImprove Clinic - ${sessionTitle} ${doctorTechnician}`,
			isTeamMeeting: isTeamMeeting
		},
		emailsToSend: emails
	};
	return processedData;
};

export const hideFieldsFromSessionPreview = (fields: FormatedFields, item: any) => {
	return fields.filter((field) => {
		if (field.field === "is_all_day") {
			return false;
		}
		if (field.field === "internal_meetings_participants") {
			const participants = item[field.field] || [];
			return participants.length > 0;
		}
		return true;
	});
};

export const calendarFieldsQuery = [
	"id",
	"treatment.client.id",
	"treatment.client.client_id",
	"treatment.client.user_id.first_name",
	"treatment.client.user_id.last_name",
	"treatment.client.user_id.email",
	"treatment.client.user_id.phone",
	"treatment.treatment_status_new",
	"title",
	"start_date",
	"end_date",
	"status",
	"service.id",
	"service.color",
	"service.name",
	"technician.color",
	"is_all_day",
	"is_first_session",
	"technician.id",
	"technician.user_id.avatar",
	"technician.user_id.id",
	"internal_meetings_participants.technicians_id.user_id.avatar",
	"treatment.id",
	"lead.id",
	"lead.phone",
	"lead.first_name",
	"lead.last_name",
	"clinic_id.id",
	"clinic_id.name",
	"notes",
	"meeting_id",
	"meeting_url",
	"service.can_be_online"
].join(",");

export const removeRedundantCancelledSessions = (sessions: Array<Session>) => {
	if (!sessions.length) return sessions;

	const redundantStatuses = ["cancelled", "staff_unavailable", "missed"];
	const sessionsPerDay = groupBy(sessions, (session) => {
		if (!session?.start_date) return "no_date";
		try {
			return format(parseISO(session.start_date), "yyyy-MM-dd");
		} catch {
			return "invalid_date";
		}
	});
	const sessionsToRemoveId = new Set<number>();

	for (const day of Object.values(sessionsPerDay)) {
		if (day.length <= 1) continue;

		const nonRedundantSessions = day.filter((session) => !redundantStatuses.includes(session?.status));
		if (nonRedundantSessions.length === 0) continue;

		const nonRedundantSessionsServicesIds = new Set(nonRedundantSessions.map((session) => session?.service?.id));

		for (const session of day) {
			if (
				redundantStatuses.includes(session?.status) &&
				nonRedundantSessionsServicesIds.has(session?.service?.id)
			) {
				sessionsToRemoveId.add(session.id);
			}
		}
	}
	const sessionsToKeep = sessions.filter((s) => !sessionsToRemoveId.has(s.id));
	return sessionsToKeep;
};
